/**
 * Enum to distinguish regular walls from partition walls.
 */
export enum EWall {
  STANDARD = "standard",
  PARTITION = "partition",
}

/**
 * Enum representing the direction in which the door will open.
 */
export enum EOpeningDirection {
  RIGHT = "right",
  LEFT = "left",
}

/**
 * Enum representing the orientation of the door's opening mechanism.
 */
export enum EOrientation {
  INWARD = "inward",
  OUTWARD = "outward",
}

/**
 * Enum representing the currently active modal window.
 */
export enum EModal {
  NONE = "none",
  CREATE_NEW = "createNew",
  PREDEFINED_ROOMS = "predefinedRooms",
  SELECT_ROOM_SIZE = "selectRoomSize",
  DOWNLOAD = "download",
  HELP = "help",
  ATTACHMENT_ATTRIBUTE = "attachmentAttribute"
}

/**
 * Enum representing the current active sub-menu, referencing a modal window, dropdown, or other type of submenu for the toolbar.
 */
export enum ESubMenu {
  NONE = "none",
  MODE = "mode",
  ARCHITECTURE = "architecture",
  TEXTURES = "textures",
  ROOM_COMPONENTS = "products",
  LEVEL = "level",
  BEHAVIOR = "behavior",
  VISIBILITY = "visibility",
  HELP = "help",
  CREATE_ELEMENTS = "createElements",
}

/**
 * Enum representing the different modes corresponding to the architecture submenu.
 */
export enum EWallMode {
  NONE = "none",
  DOOR = "door",
  PASSAGE = "passage",
  WINDOW = "window",
  PARTITION = "partition",
  RADIATOR = "radiator"
}

/**
 * Enum representing different interaction modes.
 */
export enum EInteractionMode {
  MOVE = "move",
  SELECT = "select",
  DRAW = "draw",
  ATTACHMENT = "attachment",
  MEASURE = "measure",
  COMMENT = "comment",
  SET_TEXTURE= "setTexture",
  NONE = "none",
}

/**
 * Enum to indicate the active drawer of the selection handlers.
 */
export enum EActiveDrawer {
  NONE = "none",
  WALL = "wall",
  DOOR = "door",
  PASSAGE = "passage",
  WINDOW = "window",
  FLOOR = "floor",
  ARCHITECTURE = "architecture",
  TEXTURES = "textures",
  PRODUCTS = "products",
  PARTITION = "partition",
  ROOM_COMPONENTS = "roomComponents",
  SIMPLE_MEASUREMENT = "simpleMeasurement",
  RADIATOR = "radiator",
  CREATE_ELEMENTS = "createElements",
  ELEMENT = "element",
  MODEL2D = "model2d",
  ATTACHMENT = "attachment",
  HISTORY = "HISTORY",
}

/**
 * Enum to differentiate between the two wall input types.
 */
export enum EWallInput {
  LENGTH = "length",
  DEPTH = "depth",
}

/**
 * Enum to differentiate between the two different unit systems.
 */
export enum EUnitType {
  METRIC = "metric",
  IMPERIAL = "imperial",
}

/**
 * Enum to represent the different possible orientations for a page when exporting to PDF.
 * This one is used after parsing from EPageOrientationInputParameter, removing the "auto" value.
 * These 2 separate enums are needed because jsPDF-library only differentiates between portrait and landscape,
 * and we need an auto value for the input parameter.
 */
export enum EPageOrientation {
  LANDSCAPE = "landscape",
  PORTRAIT = "portrait",
}

/**
 * Enum to represent the different possible orientations for a page when exporting to PDF.
 * Used as an input parameter, providing an auto value to automatically select the orientation,
 * based on the building's aspect ratio. Gets parsed into EPageOrientation.
 */
export enum EPageOrientationInputParameter {
  AUTO = "auto",
  PORTRAIT = "portrait",
  LANDSCAPE = "landscape",
}

/**
 * Enum to represent different units for creating a new jsPDF object.
 */
export enum IPdfUnit {
  EM = "em",
  IN = "in",
  PT = "pt",
  PX = "px",
  MM = "mm",
  CM = "cm",
  EX = "ex",
  PC = "pc",
  UNDEFINED = "undefined",
}

/**
 * Enum to represent the different possible image quality modes for exporting to PDF.
 * Has a numerical value attached to it, that gets used for image resolution upscaling.
 */
export enum EImageQuality {
  LOW = 4,
  MEDIUM = 8,
  HIGH = 16,
}

/**
 * Enum to represent the quality levels for exporting a plan to pdf/image.
 * Has a numerical value attached to it, which indicates the pixels per decimeter of the exported pdf.
 */
export enum EDownloadQualityLevels {
  LOW = 20,
  MEDIUM = 40,
  HIGH = 80,
}

/**
 * Enum to represent the different available file type extensions that can be used when exporting a plan.
 */
export enum EExportImageFileType {
  PNG = "png",
  JPEG = "jpeg",
  WEBP = "webp",
}

/**
 * Enum to represent the different available export types for exporting a plan.
 */
export enum EExportType {
  NONE = "none",
  PDF = "pdf",
  IMG = "img",
  IFC = "ifc",
}

/**
 * Enum representing the different types of draggable points.
 */
export enum EDraggablePointType {
  START = "start",
  END = "end",
  WALL = "wall",
}

/**
 * Enum to differentiate between the individual toolbar buttons by their Id in a clear way.
 */
export enum EToolbarButton {
  MODE = 0,
  ROOM_CREATION,
  DRAWING,
  ARCHITECTURE,
  TEXTURES,
  ROOM_COMPONENTS,
  BEHAVIOR,
  LEVEL,
  VISIBILITY,
  CREATE_ELEMENTS,
  EXTEND,
  MEASURE, 
  ATTACHMENT,
  HISTORY
}

/**
 * Enum to differentiate between the orientations of a room.
 */
export enum EWindingOrder {
  CLOCKWISE = "clockwise",
  COUNTERCLOCKWISE = "counterclockwise",
}

/**
 * Enum to differentiate between the elements inside of the MultiToggle component.
 */
export enum EMultiToggleElements {
  WALL_INNER_LENGTH = 0,
  WALL_OUTER_LENGTH,
  ELEMENT_DISTANCES,
  WINDOW_LENGTH,
  DOOR_LENGTH,
  RADIATOR_LENGTH,
  ROOM_DETAILS,
  ANGLES,
  ROOM_COMPONENT_DETAILS,
  MEASUREMENTS,
  GENERAL_INFO,
  HOTSPOTS,
  ELEMENT_DETAILS,
  CAMERA_POSITION_SPRITE,
  PASSAGE_LENGTH
}

/**
 * Enum to differentiate between the three modes for the MultiDirectionalNumberInput.
 * Is used to allow for doors, walls, and windows to have their length altered in multiple ways.
 */
export enum EDirection {
  LEFT = "left",
  RIGHT = "right",
  BOTH = "both",
}

/**
 * Enum to differentiate between the four different directions after which elements can align themselves to.
 * - leftToRight - "lr"
 * - topToBottom = "tb",
 * - rightToLeft = "rl",
 * - bottomToTop = "bt"
 */
 export enum EElementDirection {
  LEFT_TO_RIGHT = "lr",
  TOP_TO_BOTTOM = "tb",
  RIGHT_TO_LEFT = "rl",
  BOTTOM_TO_TOP = "bt",
}


/**
 * Enum to represent the two different types of progress components and the possibly undefined initial state.
 * - determinate - controlled progress bar, that accurately displays the progress.
 * - indeterminate - uncontrolled progress bar, that only shows when the loading has finished.
 */
export enum EProgressVariant {
  "indeterminate",
  "determinate",
  undefined,
}

/**
 * Enum to differentiate between the two types of progress bar.
 * - circular - displays a circular progress bar as a loading spinner.
 * - linear - displays a linear progress bar as a straight line.
 */
export enum EProgressType {
  circular = "circular",
  linear = "linear",
}

/**
 * Enum representing different types of elements that can be created.
 */
export enum EElementType {
  RECTANGLE = "rectangle",
  CIRCLE = "circle",
  TRIANGLE = "triangle"
}
